import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CurrencyInput } from '@/components/ui/currency-input';
import { PercentageInput } from '@/components/ui/percentage-input';
import { Info } from 'lucide-react';
import { formatCurrencyNoDecimals } from '@/utils/formatters';
import type { DrawdownParameters, CallType } from './drawdownCreateTypes';
import type { Fund } from '@/types/fund';

interface DrawdownCreateParametersStepProps {
  parameters: DrawdownParameters;
  fund: Fund;
  hasAttemptedNext: boolean;
  fieldErrors: Record<string, string>;
  onUpdate: (updates: Partial<DrawdownParameters>) => void;
}

export function DrawdownCreateParametersStep({
  parameters,
  fund,
  hasAttemptedNext,
  fieldErrors,
  onUpdate,
}: DrawdownCreateParametersStepProps) {
  return (
    <div className="space-y-8">
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Basic Parameters</h3>
          <p className="text-sm text-muted-foreground">
            Configure the fundamental parameters for this capital call
          </p>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fund</Label>
              <Input
                value={fund.display_name || fund.name}
                readOnly
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="callType">Call Type *</Label>
              <Select
                value={parameters.callType}
                onValueChange={(value: CallType) =>
                  onUpdate({ callType: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">
                    Percentage of Commitments
                  </SelectItem>
                  <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="pb-1">
              <Label htmlFor="inputValue">
                {parameters.callType === 'percentage' ? 'Percentage' : 'Amount'}{' '}
                *
              </Label>
              {parameters.callType === 'percentage' ? (
                <PercentageInput
                  value={String(parameters.inputValue)}
                  onChange={(value) => onUpdate({ inputValue: Number(value) })}
                  placeholder="Enter percentage (e.g. 25.5 for 25.5%)"
                  decimalPlaces={2}
                  showSymbol={true}
                  className={
                    hasAttemptedNext && fieldErrors.inputValue
                      ? 'border-red-500'
                      : ''
                  }
                />
              ) : (
                <CurrencyInput
                  value={parameters.inputValue}
                  onChange={(value) => onUpdate({ inputValue: value ?? 0 })}
                  currency={parameters.currency}
                  placeholder="Enter amount"
                  className={
                    hasAttemptedNext && fieldErrors.inputValue
                      ? 'border-red-500'
                      : ''
                  }
                />
              )}
              {hasAttemptedNext && fieldErrors.inputValue && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.inputValue}
                </p>
              )}
            </div>

            <div>
              <Label>Currency</Label>
              <Input
                value={parameters.currency}
                readOnly
                className="bg-muted"
                placeholder="Fund currency"
              />
            </div>
          </div>

          {fund.minimum_prepayment_pool && (
            <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                This fund has a minimum prepayment of{' '}
                {formatCurrencyNoDecimals(
                  fund.minimum_prepayment_pool,
                  parameters.currency
                )}{' '}
                per investor. Prepayment pools are handled automatically.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Timing and Details</h3>
          <p className="text-sm text-muted-foreground">
            Set the dates and description for this capital call
          </p>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="issueDate">Issue Date *</Label>
              <Input
                type="date"
                value={parameters.issueDate}
                onChange={(e) => onUpdate({ issueDate: e.target.value })}
                className={
                  hasAttemptedNext && fieldErrors.issueDate
                    ? 'border-red-500'
                    : ''
                }
              />
              {hasAttemptedNext && fieldErrors.issueDate && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.issueDate}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="dueDate">Due Date *</Label>
              <Input
                type="date"
                value={parameters.dueDate}
                onChange={(e) => onUpdate({ dueDate: e.target.value })}
                className={
                  hasAttemptedNext && fieldErrors.dueDate
                    ? 'border-red-500'
                    : ''
                }
              />
              {hasAttemptedNext && fieldErrors.dueDate && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.dueDate}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="purpose">Title *</Label>
            <Input
              value={parameters.purpose}
              onChange={(e) => onUpdate({ purpose: e.target.value })}
              placeholder="e.g., Acquisition of Company X, Working capital, etc."
              className={
                hasAttemptedNext && fieldErrors.purpose ? 'border-red-500' : ''
              }
            />
            {hasAttemptedNext && fieldErrors.purpose && (
              <p className="text-sm text-red-600 mt-1">{fieldErrors.purpose}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              value={parameters.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Additional details about this capital call..."
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
